/* eslint-disable react-refresh/only-export-components -- exports the useToast hook alongside the provider */
import React, { createContext, useCallback, useContext, useState } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import IconButton from './IconButton';

// Toast notifications. Mount <ToastProvider> once near the root, then call
// `useToast().push({ kind, message })` from anywhere. The visible region is
// rendered via portal-equivalent fixed positioning so it floats above modals.
//
//   kind ∈ "success" | "error" | "info"
//   duration: ms before auto-dismiss; 0 = sticky
//   title, art: optional bold first line and an illustration node that
//     replaces the kind icon (Hollerpost product mode uses Nugget here)
//
// Live region uses aria-live="polite" so screen readers announce without
// stealing focus. Errors use role="alert" for assertive announcement.

const ToastContext = createContext(null);

const KIND_CONFIG = {
  success: {
    icon: CheckCircle2,
    bg: 'bg-green-50',
    border: 'border-green-200',
    fg: 'text-green-800',
    iconFg: 'text-green-600',
  },
  error: {
    icon: AlertCircle,
    bg: 'bg-red-50',
    border: 'border-red-200',
    fg: 'text-red-800',
    iconFg: 'text-red-600',
  },
  info: {
    icon: Info,
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    fg: 'text-blue-800',
    iconFg: 'text-blue-600',
  },
};

let nextId = 1;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    ({ kind = 'info', message, duration = 4000, title = null, art = null }) => {
      const id = nextId++;
      setToasts((prev) => [...prev, { id, kind, message, title, art }]);
      if (duration > 0) {
        window.setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss],
  );

  const value = { push, dismiss };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex flex-col items-center gap-2 px-4 sm:items-end sm:right-4 sm:left-auto sm:bottom-4"
      >
        {toasts.map((t) => {
          const cfg = KIND_CONFIG[t.kind] || KIND_CONFIG.info;
          const Icon = cfg.icon;
          return (
            <div
              key={t.id}
              role={t.kind === 'error' ? 'alert' : 'status'}
              className={[
                'pointer-events-auto w-full sm:max-w-sm rounded-xl border shadow-lg p-3 flex items-start gap-2',
                cfg.bg,
                cfg.border,
                'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2',
              ].join(' ')}
            >
              {t.art ? <span className="flex-shrink-0" aria-hidden="true">{t.art}</span> : <Icon className={`w-5 h-5 flex-shrink-0 ${cfg.iconFg}`} aria-hidden="true" />}
              {t.title ? (
                <div className={`flex-1 text-sm ${cfg.fg}`}>
                  <p className="hp-display font-black text-base">{t.title}</p>
                  <p className="font-medium">{t.message}</p>
                </div>
              ) : (
                <p className={`flex-1 text-sm font-medium ${cfg.fg}`}>{t.message}</p>
              )}
              <IconButton
                icon={X}
                label="Dismiss notification"
                onClick={() => dismiss(t.id)}
                className="-mt-1 -mr-1"
                size="sm"
              />
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Don't throw — most callers can degrade silently if provider isn't
    // mounted yet (e.g. early-mount race). Log so dev can spot it.
    if (process.env.NODE_ENV !== 'production') {
      console.warn('useToast() called without <ToastProvider> ancestor');
    }
    return { push: () => 0, dismiss: () => {} };
  }
  return ctx;
};

export default ToastProvider;
